import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Layout, Row, Col } from "antd";
import { ModalComponent, CustomToolTip, Text, SwitchComponent } from "@iiht/components";
import { MmlLogo, HelpLogo, LogoutLogo, Sun, Moon, BlueSun, WhiteMoon, Exclamation, CartLogo } from "../../../icons";
import Notification from "./subComponents/Notification/notification";
import { retrieveUserCartDetails, setIsDarkTheme } from "../../../store/actions";
import { checkIfB2CUser, getEnv, getUserRole } from "../../../utils/genericUtils";
import { translator } from "../../../constants/actionTypes";
import { authTypes, DOC, helpItems, roleName } from "../../../constants/componentConstants";
import { ROUTES } from "../../../constants/routesConstants";
import { localStorageHelper, directLoginStorage } from "../../../utils/storageHelper";
import "./headerStyle.scss";

const { Header } = Layout;

const HeaderView = ({ logOutHandler }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const [userName, setUserName] = useState("");
  const [isConfirmModalVisible, setConfirmModalVisible] = useState(false);
  const [showHelpDrawer, setShowHelpDrawer] = useState(false);
  const refElement = useRef(null);
  const logo_path = localStorageHelper.get("logo_path");
  const userId = localStorageHelper.get("userId");
  const isMarkForB2c = checkIfB2CUser();
  const role = getUserRole();
  const currentUrl = window?.location?.href;
  const isPooled = directLoginStorage.get("isDemandLogin");
  const isB2CTenantUser = checkIfB2CUser();

  const { isDarkTheme } = useSelector(({ common }) => common);
  const { userCartDetails } = useSelector((state) => state.b2c);

  useEffect(() => {
    setUserName(localStorageHelper.get("first_name"));
  }, []);

  useEffect(() => {
    if (userId && isB2CTenantUser) {
      dispatch(retrieveUserCartDetails(userId));
    }
  }, [userId]);

  const handleLogout = () => {
    setConfirmModalVisible(false);
    logOutHandler();
  };

  useEffect(() => {
    const ClickedOutsideHandler = (e) => {
      if (showHelpDrawer && refElement?.current && !refElement?.current?.contains(e.target)) {
        setShowHelpDrawer(false);
      }
    };
    document?.addEventListener("mousedown", ClickedOutsideHandler);
    return () => {
      document?.removeEventListener("mousedown", ClickedOutsideHandler);
    };
  }, [showHelpDrawer]);

  const handleConfirmModalCancel = () => {
    setConfirmModalVisible(false);
  };

  const handleLogoutClick = () => {
    setConfirmModalVisible(true);
  };

  const handleLightMode = () => {
    dispatch(setIsDarkTheme(false));
  };

  const handleDarkMode = () => {
    dispatch(setIsDarkTheme(true));
  };

  const ProfilePageHandler = () => {
    history.push(`${ROUTES.PROFILE}/${userId}`);
  };

  const helpHandler = () => {
    setShowHelpDrawer(!showHelpDrawer);
  };

  const helpPageHandler = (id) => {
    if (id === 1) {
      history.push(ROUTES?.API_HELP);
      setShowHelpDrawer(false);
    }
  };

  const cartHandler = () => {
    history.push(ROUTES.B2C_PAYMENT);
  };

  const handleLogoClick = () => {
    const isMentor = JSON.parse(localStorageHelper.get("is_mentor") || "false");
    if (isMentor) {
      history.push(ROUTES.WORKSPACE_MANAGER);
    } else {
      history.push("/dashboard");
    }
  };

  return (
    <Header className="header">
      <Row>
        <Col md={7} lg={6} xl={6}>
          <div className="logo" onClick={handleLogoClick} style={{ cursor: "pointer" }}>
            {logo_path && role === roleName?.tenantUser ? (
              <img
                src={
                  (window?.location?.origin &&
                    !window?.location?.origin?.includes("localhost") &&
                    `${window?.location?.origin?.toString()}${logo_path}`) ||
                  `${translator?.baseURL}${logo_path}`
                }
                alt="logo"
                className={"mml-logo"}
              />
            ) : (
              <MmlLogo className="iiht-mml-logo" />
            )}
          </div>
        </Col>
        <Col md={{ span: 10, offset: 5 }} lg={{ span: 7, offset: 10 }} xl={{ span: 7, offset: 10 }} className="display-flex">
          <Col md={3} lg={3} xl={3} sm={3}>
            {!currentUrl?.includes(`/${DOC}`) && isDarkTheme ? (
              <div className={`theme-toggle-container header-option toggle-theme ${isDarkTheme ? "dark-mode" : "light-mode"}`}>
                <Sun className="pointer-cursor iiht-svg-icon sun_icon" onClick={handleLightMode} />
              </div>
            ) : (
              <div className={`theme-toggle-container header-option toggle-theme ${isDarkTheme ? "dark-mode" : "light-mode"}`}>
                <WhiteMoon onClick={handleDarkMode} className="mb2 pointer-cursor iiht-svg-icon moon_icon" />
              </div>
            )}
          </Col>

          {isMarkForB2c && (
            <Col md={3} lg={3} xl={3} >
              <div className="header-option cart-container">
                <div className="cart-number">
                  {userCartDetails?.data?.cart_details?.length > 0 ? userCartDetails?.data?.cart_details?.length : 0}
                </div>
                <CartLogo className="pointer-cursor cart-logo" onClick={cartHandler} />
              </div>
            </Col>
          )}
          <Col md={3} lg={3} xl={3}>
            <div className="header-option" ref={refElement}>
              <HelpLogo className="help-logo pointer-cursor" onClick={helpHandler}></HelpLogo>
              {showHelpDrawer && (
                <div className="help-container">
                  <div className="display-flex flex-direction-column help-items">
                    {helpItems?.map((item, index) => (
                      <Text
                        className={`item pointer-cursor ${index === 0 ? "disabled-item" : null} ${index === 1 && (role === roleName?.tenantUser || role === roleName?.tenantadmin)
                          ? "display-none"
                          : "display-block"
                          }`}
                        key={index}
                        onClick={() => helpPageHandler(item?.id)}
                      >
                        {item?.text}
                      </Text>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Col>
          <Col md={3} lg={3} xl={3}>
            <div className="header-option">
              <Notification className="notification-logo"></Notification>
            </div>
          </Col>
          <Col md={9} lg={9} xl={9} className="user-info">
            <div className="welcome-container ml2">
              <Text className="label-regular welcome-title">{t("common.welcome")}</Text>
            </div>
            <div className="user-name ml2">
              <CustomToolTip title={t(userName)}>
                <Text className="normal-text welcome-title pointer-cursor" onClick={ProfilePageHandler}>
                  {t(userName)}
                </Text>
              </CustomToolTip>
            </div>
          </Col>
          {(!isPooled && (
            <Col md={3} lg={3} xl={3}>
              <CustomToolTip title={t("common.logout")}>
                <LogoutLogo
                  className={`${isB2CTenantUser === true ? "qa-logout-btn logout-btn mt4" : "qa-logout-btn logout-btn mt4 ml11"}`} onClick={() => handleLogoutClick()} />
              </CustomToolTip>
            </Col>
          )) ||
            null}
        </Col>
      </Row>
      {
        isConfirmModalVisible && (
          <ModalComponent
            width="32.5rem"
            visible={isConfirmModalVisible}
            handleCancel={handleConfirmModalCancel}
            closable
            bodyCls="modal-responsive-body"
          >
            <ModalComponent.Icon>
              <Exclamation />
            </ModalComponent.Icon>
            <ModalComponent.H1>{t("common.logout")}</ModalComponent.H1>
            <ModalComponent.Text>{t("header.logoutMsg")}</ModalComponent.Text>
            <div className="modal-btn-wrapper">
              <ModalComponent.Cancel onClick={handleConfirmModalCancel}>{t("common.cancelText")}</ModalComponent.Cancel>
              <ModalComponent.Proceed onClick={handleLogout}>{t("common.logout")}</ModalComponent.Proceed>
            </div>
          </ModalComponent>
        )
      }
    </Header >
  );
};

export default HeaderView;
